import { Helmet } from 'react-helmet'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HashLink as Link } from 'react-router-hash-link'
import { motion, useAnimationControls } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainHeader from '../../../components/MainHeader'
import MainFooter from '../../../components/MainFooter'
import CTAButton from '../../../components/CTAButton'
import AnimatedQuote from '../../../components/AnimatedQuote'
import PricingCard, { BundleCard } from '../../../components/PricingCard'
import { boxvar1, boxvar3, boxvar2_1, boxvar2_2, boxvar2dyndelay, oPU } from '../../../core.js'
import { faPhone, faChevronDown, faMagnifyingGlassArrowRight, faTags } from '@fortawesome/free-solid-svg-icons'
import packageJson from '../../../../package.json'

import testimonial_joely from '../../../assets/pictures/testimonials/testimonial_joely.webp'
import iphonecam_contentcreator from '../../../assets/pictures/sectionimages/iphonecam_contentcreator-cut_compressed.webp'

import '../../Pricing/Pricing.css'
import '../../Pricing/MPricing.css'
import '../../Home/Home.css'
import '../../Home/MHome.css'
import './FunnelVeranstalter.css'
import './MFunnelVeranstalter.css'
import useProgressiveHeroBg from '../../../hooks/useProgressiveHeroBg'

const scrolldowniconanim = {
    idle: {
        y: [0, 6, 0],
        scale: 1,
        transition: { duration: 1.5, repeat: Infinity, ease: 'easeOut' },
    },
    hover: {
        y: [0, 3, 0],
        scale: 1.2,
        transition: { duration: 1.0, repeat: Infinity, ease: 'easeInOut' },
    },
}

const CALENDLY = 'https://calendly.com/tonetworkde-meet/getintouch'
const STAND = packageJson.pricingdate
const SITE = 'https://tonetwork.de'

const REGIONS = {
    memmingen: {
        city: 'Memmingen',
        region: 'Unterallgäu',
        canonical: '/landing/event-memmingen',
        seoTitle: 'Eventfotografie & Marketing Memmingen – für Veranstalter | ToNetwork',
        seoDescription: 'Event-Boost für Veranstalter in Memmingen: Landingpage, Event-Fotos und Social Content ab 1.490 €. ToNetwork aus dem Unterallgäu – Listenpreise, individuelles Angebot.',
        h1: 'Events in Memmingen – online sichtbar, live unvergesslich',
        h1sub: 'Landingpage, Event-Fotos und Reichweite – aus einer Hand für Veranstalter.',
        localLead: 'Wir begleiten Veranstalter in Memmingen und im Unterallgäu – von der Event-Landingpage über professionelle Fotos bis zum Social-Media-Push vor dem nächsten Termin.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Hawangen', 'Babenhausen', 'Türkheim'],
    },
    unterallgaeu: {
        city: 'Unterallgäu',
        region: 'Allgäu',
        canonical: '/landing/event-unterallgaeu',
        seoTitle: 'Eventfotografie Unterallgäu – Paket für Veranstalter ab 1.490 € | ToNetwork',
        seoDescription: 'Event-Boost im Unterallgäu: Event-Landingpage, Event-Fotopaket und 1 Monat Social Content. Partner aus Hawangen – transparente Preise, kostenloses Erstgespräch.',
        h1: 'Dein Event im Unterallgäu – digital starten, visuell begeistern',
        h1sub: 'Webseite, Bilder und Reichweite im Komplettpaket – damit dein nächstes Event ausverkauft ist.',
        localLead: 'Partys, Festivals, Vereinsfeste oder Community-Events: Wir kennen die Region und wissen, was Veranstalter brauchen – ohne Agentur-Overhead.',
        areaServed: ['Unterallgäu', 'Memmingen', 'Türkheim', 'Mindelheim', 'Bad Wörishofen'],
    },
    allgaeu: {
        city: 'Allgäu',
        region: 'Schwaben',
        canonical: '/landing/event-allgaeu',
        seoTitle: 'Eventfotografie Allgäu – Marketing & Fotos für Veranstalter | ToNetwork',
        seoDescription: 'Event-Boost im Allgäu für Veranstalter: Landingpage, Event-Fotos und Social Content ab 1.490 €. ToNetwork – All-in-One aus dem Unterallgäu.',
        h1: 'Events im Allgäu – online pushen, live einfangen',
        h1sub: 'Landingpage, Event-Fotos und Social Content – der Rundum-Start für Veranstalter.',
        localLead: 'Ob Memmingen, Kempten oder das ländliche Allgäu: Ein Ansprechpartner für Event-Webseite, Fotografie und Reichweite – persönlich und pragmatisch.',
        areaServed: ['Allgäu', 'Memmingen', 'Kempten', 'Unterallgäu', 'Oberallgäu'],
    },
    kempten: {
        city: 'Kempten',
        region: 'Allgäu',
        canonical: '/landing/event-kempten',
        seoTitle: 'Eventfotografie Kempten – Paket für Veranstalter ab 1.490 € | ToNetwork',
        seoDescription: 'Event-Boost in Kempten und Umgebung: Event-Landingpage, Event-Fotopaket und Social Content. ToNetwork – persönlicher Partner aus dem Allgäu.',
        h1: 'Events in Kempten & Allgäu – sichtbar machen, Stimmung festhalten',
        h1sub: 'Professioneller Auftritt online und starke Bilder live – für Veranstalter, die wachsen wollen.',
        localLead: 'Für Veranstalter in Kempten und der Region: kurze Wege, direkte Kommunikation und Pakete, die genau zu deinem Event passen.',
        areaServed: ['Kempten', 'Allgäu', 'Memmingen', 'Sonthofen', 'Immenstadt'],
    },
    default: {
        city: 'Memmingen',
        region: 'Allgäu',
        canonical: '/funnel/veranstalter',
        seoTitle: 'Event-Boost für Veranstalter – Landingpage, Fotos & Social | ToNetwork',
        seoDescription: 'Event-Boost von ToNetwork: Event-Landingpage, Event-Fotopaket und 1 Monat Social Content ab 1.490 €. Für Veranstalter in Memmingen, Unterallgäu und Allgäu.',
        h1: 'Dein Event verdient mehr als nur Flyer',
        h1sub: 'Landingpage, Event-Fotos und Reichweite – der Rundum-Start für Veranstalter.',
        localLead: 'Aus Hawangen im Unterallgäu begleiten wir Veranstalter in Memmingen, im Allgäu und darüber hinaus – Web, Media und Marketing aus einer Hand.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Allgäu', 'Kempten'],
    },
}

function resolveRegion(pathname) {
    if (pathname.includes('memmingen')) return REGIONS.memmingen
    if (pathname.includes('unterallgaeu')) return REGIONS.unterallgaeu
    if (pathname.includes('allgaeu')) return REGIONS.allgaeu
    if (pathname.includes('kempten')) return REGIONS.kempten
    return REGIONS.default
}

const bundleEvent = {
    name: 'Event-Boost',
    featured: true,
    price: 'ab 1.490 €',
    priceExtra: 'einmalig',
    contains: 'Event-Landingpage + Event-Fotopaket + 1 Monat Social Content (Starter DIY)',
    sales: 'Euer Event online ausverkauft – Webseite, Bilder und Reichweite im Komplettpaket.',
    paket: 'bundle-event',
}

const webBusiness = {
    name: 'Business',
    badge: 'Landingpage',
    price: 'ab 2.190 €',
    priceNote: 'einmalig',
    forwho: 'KMU & Veranstalter',
    sales: 'Eure Event-Landingpage soll arbeiten, nicht nur hübsch sein. Individuell gestaltet, auf Google gefunden, gebaut, um Besucher zu gewinnen.',
    features: [
        { t: 'bis 6 Seiten', in: null },
        { t: 'Individuelles Design', in: null },
        { t: 'Mobiloptimiert', in: true },
        { t: 'SEO optimiert', in: true },
        { t: 'Kontaktformular', in: true },
        { t: 'Blog / News', in: true },
        { t: 'Shop / Buchung / Login', in: false },
        { t: '2 Revisionen · fertig in ~4 Wochen', in: null },
    ],
    paket: 'web-business',
}

const fotoEvent = {
    name: 'Event-Paket',
    badge: 'Allrounder',
    featured: true,
    price: 'ab 590 €',
    priceNote: 'einmalig',
    forwho: 'Veranstaltungen & Partys',
    sales: 'Eure Veranstaltung verdient Bilder, die die Stimmung einfangen. Wir sind mittendrin – ihr bekommt Fotos, die Lust auf das nächste Event machen.',
    features: [
        { t: 'bis 5 Std. Dauer', in: null },
        { t: 'bis 100 bearbeitete Bilder', in: null },
        { t: 'Express-Lieferung in 48 Std.', in: true },
        { t: 'Nutzungsrechte inklusive', in: true },
    ],
    paket: 'foto-event',
}

const painPoints = [
    {
        title: 'Das Event wird online nicht wahrgenommen',
        text: 'Keine Landingpage, keine Social-Media-Strategie, kein Google-Eintrag – potenzielle Besucher erfahren zu spät, dass du ein Event planst.',
    },
    {
        title: 'Keine professionellen Bilder danach',
        text: 'Handy-Schnappschüsse reichen nicht. Ohne starke Fotos fehlt dir Material für Social Media, Sponsoren und die Bewerbung des nächsten Events.',
    },
    {
        title: 'Alles aus verschiedenen Händen',
        text: 'Webdesigner, Fotograf, Social-Media-Berater – drei Ansprechpartner, drei Rechnungen, kein roter Faden für deinen Event-Auftritt.',
    },
]

const faqsForRegion = (city) => [
    {
        q: `Was kostet Event-Marketing für Veranstalter in ${city}?`,
        a: 'Unser Bundle „Event-Boost" startet ab 1.490 € einmalig und enthält Event-Landingpage, Event-Fotopaket und 1 Monat Social Content (Starter DIY). Alle Preise sind Listenpreise ohne Rabatte – nach einem kostenlosen Erstgespräch erhältst du ein individuelles Festangebot.',
    },
    {
        q: `Begleitet ihr auch Events außerhalb von ${city}?`,
        a: 'Ja. Wir sitzen in Hawangen bei Memmingen und arbeiten mit Veranstaltern im gesamten Unterallgäu, Allgäu und darüber hinaus – vor Ort bei deinem Event oder per Video für die Planung.',
    },
    {
        q: 'Kann ich einzelne Leistungen statt des Bundles buchen?',
        a: 'Ja. Event-Landingpage (Business), Event-Fotopaket und Social Content sind auch einzeln buchbar. Das Bundle spart dir Koordinationsaufwand und ist unser Komplettpaket für Veranstalter.',
    },
    {
        q: 'Wie schnell bekomme ich die Event-Fotos?',
        a: 'Im Event-Paket ist Express-Lieferung in 48 Stunden inklusive – damit du die Bilder direkt für Social Media und die Bewerbung deines nächsten Events nutzen kannst.',
    },
]

function localBusinessJsonLd(region) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'ToNetwork UG (haftungsbeschränkt)',
        description: region.seoDescription,
        url: SITE,
        telephone: '+49-8332-4908872',
        email: 'hey@tonetwork.de',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Hawangen',
            addressLocality: 'Hawangen',
            postalCode: '87749',
            addressRegion: 'Bayern',
            addressCountry: 'DE',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 47.998,
            longitude: 10.283,
        },
        areaServed: region.areaServed.map((name) => ({ '@type': 'City', name })),
        priceRange: '€€',
        knowsAbout: ['Eventfotografie', 'Event-Marketing', 'Webentwicklung', 'Social Media', 'Content Creation'],
    }
}

export default function FunnelVeranstalter() {
    const { pathname } = useLocation()
    const region = resolveRegion(pathname)
    const canonicalUrl = SITE + region.canonical
    const faqs = faqsForRegion(region.city)

    const controls = useAnimationControls()

    useEffect(() => {
        controls.start('idle')
    }, [controls])

    useProgressiveHeroBg('sec_start--funnel-veranstalter')

    return (
        <div className="mainCon funnel_veranstalter" id="mainCon--funnel-veranstalter">

            <MainHeader funnel disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>{region.seoTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={region.seoDescription} />
                <meta name="keywords" content={`Eventfotografie ${region.city}, Event Marketing ${region.city}, Veranstalter ${region.region}, Event-Boost, ToNetwork`} />
                <meta property="og:title" content={region.seoTitle} />
                <meta property="og:description" content={region.seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="de_DE" />
                <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd(region))}</script>
            </Helmet>

            <section className="sec_start progressive-hero-bg" id="sec_start--funnel-veranstalter">

                <motion.div
                    variants={boxvar1}
                    initial="hidden"
                    exit="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-50px' }}
                    className="start_heading_cont"
                >
                    <AnimatedQuote className="start_quote" text={region.h1} />

                    <p className="start_about">
                        {region.h1sub}<br />
                        <br />
                        {region.localLead}<br />
                        <br />
                        Unser Bundle: <b className="start_about_highlight">Event-Boost</b> – Landingpage, Event-Fotos & Social Content ab 1.490 €
                    </p>
                </motion.div>

                <motion.div variants={boxvar3} initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: '-50px' }}>
                    <div className="sec_start_cta_row">
                        <CTAButton className="sec_start_btn" showArrow={false} customIcon={faPhone} iconLeft={true} title="Kostenloses Erstgespräch" onclick={() => { oPU(CALENDLY) }} />
                        <CTAButton className="sec_start_btn sec_start_btn-chat" showArrow={true} lightcta={true} title="Event-Boost anfragen" href="/support?paket=bundle-event" />
                    </div>
                </motion.div>

                <Link smooth to="#sec_next--funnel-veranstalter">
                    <motion.div
                        className="sec_next_icon_div noselect"
                        onHoverStart={() => controls.start('hover')}
                        onHoverEnd={() => controls.start('idle')}
                    >
                        <span>Erfahre mehr</span>
                        <motion.div variants={scrolldowniconanim} animate={controls}>
                            <FontAwesomeIcon icon={faChevronDown} className="sec_next_icon_arrow" />
                        </motion.div>
                    </motion.div>
                </Link>

            </section>

            <section className="sec_next" id="sec_next--funnel-veranstalter">
                <div className="sec_next_cont" id="sec_next--funnel-veranstalter--cont">

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_veranstalter_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Kommt dir das bekannt vor?</h2>
                        <h3>Drei Probleme, die Veranstalter in {region.city} und Umgebung kennen.</h3>
                        <div className="funnel_veranstalter_pain_grid">
                            {painPoints.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    variants={boxvar2dyndelay}
                                    custom={0.15 * (i + 1)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, margin: '-50px' }}
                                    className="funnel_veranstalter_pain_card"
                                >
                                    <span className="funnel_veranstalter_pain_num">{String(i + 1).padStart(2, '0')}</span>
                                    <h4>{p.title}</h4>
                                    <p>{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_veranstalter_section pricing_section"
                        id="funnel-veranstalter-angebot"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Die Lösung: Event-Boost</h2>
                        <h3>Landingpage, Event-Fotos und Social Content – aus einer Hand.</h3>
                        <p className="pricing_lead">
                            Statt drei Ansprechpartner bekommst du bei ToNetwork alles in einem Paket: eine Event-Landingpage,
                            die Besucher anzieht, professionelle Fotos live vor Ort und einen Monat Social Content,
                            um dein nächstes Event schon jetzt zu bewerben.
                        </p>
                        <div className="pricing_grid funnel_veranstalter_bundle_grid">
                            <BundleCard bundle={bundleEvent} delay={0.15} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf dein Projekt zugeschnitten. Stand: {STAND}
                        </p>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_veranstalter_section pricing_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Oder einzeln buchen</h2>
                        <h3>Flexibel starten – jederzeit erweiterbar.</h3>
                        <div className="pricing_grid">
                            <PricingCard tier={webBusiness} delay={0.15} />
                            <PricingCard tier={fotoEvent} delay={0.3} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf dein Projekt zugeschnitten. Stand: {STAND}
                        </p>
                        <br />
                        <CTAButton
                            showArrow={false}
                            customIcon={faTags}
                            iconLeft={true}
                            title="Alle weiteren Pakete & Preise"
                            href="/pricing"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_2}
                        className="funnel_veranstalter_section funnel_veranstalter_why"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="funnel_veranstalter_why_text">
                            <h2>Warum ToNetwork?</h2>
                            <h3>Weil du bei uns alles bekommst – aus einer Hand.</h3>
                            <p>
                                Wir sind kein anonymer Online-Dienstleister, sondern dein persönlicher Partner aus dem Allgäu.
                                Webentwicklung, Eventfotografie und Social Content unter einem Dach – mit direkter Kommunikation
                                und Leidenschaft für Events, die man spürt.
                            </p>
                            <ul className="funnel_veranstalter_benefits">
                                <li>Event-Begleitung vor Ort – wir sind mittendrin, nicht nur danach da</li>
                                <li>Express-Lieferung der Fotos in 48 Stunden</li>
                                <li>Social Content, der Lust auf dein nächstes Event macht</li>
                                <li>Erstberatung kostenlos, Festangebot ohne versteckte Kosten</li>
                                <li>Faire Konditionen für kleine Veranstalter und Startups</li>
                            </ul>
                            <CTAButton
                                showArrow={false}
                                customIcon={faMagnifyingGlassArrowRight}
                                iconLeft={true}
                                title="Alle Leistungen ansehen"
                                href="/services"
                            />
                        </div>
                        <img
                            src={iphonecam_contentcreator}
                            alt={`Eventfotografie und Content für Veranstalter in ${region.city}`}
                            className="funnel_veranstalter_why_img"
                            loading="lazy"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_veranstalter_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Das sagen unsere Kunden</h2>
                        <div className="funnel_veranstalter_testimonial">
                            <span className="funnel_veranstalter_testimonial_quote">
                                „Dank der Fotos von David erstrahlen unsere Events in neuem Licht."
                            </span>
                            <div className="funnel_veranstalter_testimonial_person">
                                <img src={testimonial_joely} alt="Joely White, Veranstalter Queerparadise Party" />
                                <div>
                                    <span className="funnel_veranstalter_testimonial_name">Joely White</span>
                                    <span className="funnel_veranstalter_testimonial_company">Veranstalter, Queerparadise Party</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_veranstalter_section pricing_section"
                        id="funnel-veranstalter-faq"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Häufige Fragen</h2>
                        <div className="pricing_faq_list">
                            {faqs.map((f, i) => (
                                <details key={i} className="pricing_faq_item" open={i === 0}>
                                    <summary>{f.q}</summary>
                                    <p>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box small_services_box-bigbottom funnel_veranstalter_cta_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.3}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Bereit für dein nächstes Event in {region.city}?</h3>
                            <br />
                            <p>
                                In einem kostenlosen 30-Minuten-Gespräch finden wir heraus, was dein Event wirklich
                                braucht – ehrlich und ohne Verkaufsdruck. Alle Preise hier sind Listenpreise ohne Rabatte
                                und werden individuell für dich ausgestaltet.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faPhone}
                                iconLeft={true}
                                title="Kostenloses Erstgespräch"
                                onclick={() => { oPU(CALENDLY) }}
                            />
                            <br />
                            <p className="pricing_toni_hint">
                                Erst Orientierung gefragt? <a href="/chat">ToNi beantwortet Fragen zu Paketen und Preisen</a> – unverbindlich im Chat.
                            </p>
                        </div>
                    </motion.div>

                    <p className="pricing_footnote">
                        Es gelten unsere <a href="/rechtliches/agb">Allgemeinen Geschäftsbedingungen (AGB)</a>
                    </p>

                    <br /><br /><br />
                </div>
            </section>

            <MainFooter />
        </div>
    )
}
